import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useFormik } from 'formik';
import { useLazyQuery, useApolloClient } from 'react-apollo';
import propTypes from 'prop-types';
import gql from 'graphql-tag';
import * as yup from 'yup';
import firebase, { linkAccount } from '../../services/firebase';
import AuthCodeModal from '../AuthCode';
import PhoneInput from '../PhoneInput/PhoneInput';
import SocialButtons from '../SocialButtons';
import authorizationGql from '../../utils/authorization.gql';
import useToast from '../../hooks/useToast';

const meGql = gql`
  query me {
    me {
      _id
      name
      phoneNumber
      condominiums {
        data
        role
      }
    }
  }
`;

export default function AuthForm({ isLoading, setLoading }) {
  const [isModalVisible, setModalVisible] = useState(false);
  const apolloClient = useApolloClient();
  const toggleModal = () => setModalVisible(x => !x);
  const history = useHistory();
  const match = useRouteMatch();
  const toast = useToast();

  const [fetchMe] = useLazyQuery(meGql, {
    onCompleted: () => {
      history.push(match.params.slug ? `/addcondominium/${match.params.slug}` : `/dashboard`);
    },
  });

  const recaptchaVerifier = useRef();
  const submitRef = useRef();

  const confirmCode = async code => {
    const { user, additionalUserInfo } = await submitRef.current.confirm(code);
    let authorizationData;

    try {
      authorizationData = apolloClient.readQuery({ query: authorizationGql });
      // eslint-disable-next-line
    } catch (e) {}

    await linkAccount(authorizationData);

    if (additionalUserInfo.isNewUser || !user.email) {
      history.push(`/register?${match.params.slug ? `slug=${match.params.slug}` : ''}`);
    } else fetchMe();
  };

  const { handleSubmit, handleChange, values, errors, touched, isSubmitting } = useFormik({
    initialValues: { phone: '', country: '+55' },
    validationSchema: yup.object({
      country: yup
        .string()
        .required()
        .max(3),
      phone: yup
        .string()
        .test({
          message: 'invalid-phone',
          // eslint-disable-next-line no-useless-escape
          test: a => a && a.match(/(\(\d{2}\)\s)(\d{4,5}\-\d{4})/gm),
        })
        .required(),
    }),
    onSubmit: async ({ phone, country }) => {
      setLoading(true);
      const phoneStr = `${country}${phone
        .replace('(', '')
        .replace(')', '')
        .replace('-', '')
        .replace(' ', '')}`;

      try {
        submitRef.current = await firebase.auth().signInWithPhoneNumber(phoneStr, recaptchaVerifier.current);
        setModalVisible(true);
      } catch (e) {
        toast.fire({ icon: 'error', text: 'Problemas ao enviar o SMS' });
      }
      setLoading(false);
    },
  });

  useEffect(() => {
    // firebase.auth().settings.appVerificationDisabledForTesting = true;
    firebase.auth().useDeviceLanguage();

    recaptchaVerifier.current = new firebase.auth.RecaptchaVerifier('sign-in-button', {
      size: 'invisible',
    });
  }, []);

  return (
    <>
      {isModalVisible && <AuthCodeModal toggle={toggleModal} onSubmit={confirmCode} />}

      <SocialButtons />
      <form onSubmit={handleSubmit} className={isLoading ? 'is-hidden' : ''}>
        <div className="form-holder">
          <div className="form-group">
            <label>Ou insira seu celular com DDD para entrar</label>
            <PhoneInput errors={errors} handleChange={handleChange} values={values} touched={touched} />
          </div>
          <button className="btn btn-block btn-primary" type="submit" id="sign-in-button" disabled={isSubmitting}>
            {isSubmitting ? 'Entrando' : 'Confirmar'}
          </button>
        </div>
      </form>
    </>
  );
}

AuthForm.propTypes = {
  isLoading: propTypes.bool,
  setLoading: propTypes.func.isRequired,
};

AuthForm.defaultProps = {
  isLoading: false,
};
